import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { CustomFieldFilterValue, LeadQuery } from '../api/client';
import {
  useBulkAssignLeads,
  useCustomFields,
  useDistributeLeads,
  useLeadCampaigns,
  useLeadSources,
  useLeadStatuses,
  useLeadIds,
  useLeads,
  useUsers,
} from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import { ColumnPicker } from '../components/ColumnPicker';
import { DateRangeFilter, type DateRange } from '../components/DateRangeFilter';
import { HIDDEN_CUSTOM_FIELDS, useColumnPreferences } from '../lib/columns';
import { EmptyState, ErrorBanner, TableSkeleton } from '../components/ui';
import { BulkAssignModal } from './BulkAssignModal';
import { DistributeModal } from './DistributeModal';
import { NewLeadModal } from './NewLeadModal';

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 150, 200];
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_KEY = 'crm.leads.page-size';

/** Statuses nobody has worked yet — flagged red down the left of the row. */
const UNWORKED_STATUSES = new Set(['new', 'untouched']);
const UNTOUCHED = 'untouched';

/** Opened in a new tab so the list, its filters and its paging stay put. */
function openLead(id: string) {
  window.open(`/leads/${id}`, '_blank', 'noopener,noreferrer');
}

/**
 * Text fields whose filter is a fixed list instead of a free-text box.
 *
 * Payment status is a text field because the registration flow writes whatever
 * it sends ("success", "Success", "FREE"…). A select field would reject those
 * leads, so the choice is matched with `contains`, which is case-insensitive.
 */
const TEXT_FILTER_CHOICES: Record<string, Array<{ value: string; label: string }>> = {
  cf_payment_status: [
    { value: 'success', label: 'Success' },
    { value: 'pending', label: 'Pending' },
    { value: 'failed', label: 'Failed' },
    { value: 'free', label: 'Free' },
  ],
};

export function LeadsPage() {
  // Still same-tab for a lead this page just created; only list rows open
  // in a new tab.
  const navigate = useNavigate();
  const { user, can, canAny } = useAuth();
  const { data: fields = [] } = useCustomFields();
  const { data: campaigns = [] } = useLeadCampaigns();
  const { data: statuses = [] } = useLeadStatuses();
  const { data: sources = [] } = useLeadSources();

  // The assignee filter only makes sense to someone who can see other people's
  // leads, and /users is closed to everyone else anyway - so don't ask for it.
  const canFilterByAssignee =
    user?.scope !== 'own' && canAny('lead.assign', 'user.manage', 'team.manage');
  const { data: people = [] } = useUsers(true, canFilterByAssignee);

  // Active people only: the business asked for deactivated users to be left
  // out of this dropdown. (Their old leads are still found by search.)
  const assignees = useMemo(
    () => people.filter((u) => u.is_active).sort((a, b) => a.full_name.localeCompare(b.full_name)),
    [people],
  );

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string[]>([]);
  const [sourceIds, setSourceIds] = useState<string[]>([]);
  const [assignedTo, setAssignedTo] = useState<string[]>([]);
  const [unassigned, setUnassigned] = useState(false);
  const [searchParams] = useSearchParams();
  const [campaignIds, setCampaignIds] = useState<string[]>(() => {
    const c = searchParams.get('campaign');
    return c ? [c] : [];
  });
  const [cf, setCf] = useState<Record<string, CustomFieldFilterValue>>({});
  const [dateRange, setDateRange] = useState<DateRange>({});
  const [showNew, setShowNew] = useState(false);
  // A team specialist cannot open an unassigned lead, so they get this instead.
  const [createdNotice, setCreatedNotice] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [showDistribute, setShowDistribute] = useState(false);

  // Rows per page is a per-browser preference, like the column choice.
  const [pageSize, setPageSize] = useState<number>(() => {
    const stored = Number(localStorage.getItem(PAGE_SIZE_KEY));
    return PAGE_SIZE_OPTIONS.includes(stored) ? stored : DEFAULT_PAGE_SIZE;
  });

  // Untouched-lead selection: the checkbox takes the current page, the banner
  // offers everything matching the filter.
  const [untouchedMode, setUntouchedMode] = useState(false);
  const [allUntouchedSelected, setAllUntouchedSelected] = useState(false);
  const [allUnassignedSelected, setAllUnassignedSelected] = useState(false);

  const bulkAssign = useBulkAssignLeads();
  // Spans every per-person call of a multi-person assign, not just one.
  const [assigning, setAssigning] = useState(false);
  const distribute = useDistributeLeads();
  const leadIds = useLeadIds();

  // Keyset pagination: a stack of the cursors we have visited, so Previous
  // works without the API needing offsets. There is deliberately no page
  // number — the API cannot support jumping to page 17.
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1] ?? null;

  const isAssignedDate = dateRange.dateField === 'assigned';
  const query: LeadQuery = {
    limit: pageSize,
    cursor,
    search: search || undefined,
    status: status.length ? status : undefined,
    source: sourceIds.length ? sourceIds : undefined,
    assigned_to: assignedTo.length ? assignedTo : undefined,
    campaign: campaignIds.length ? campaignIds : undefined,
    unassigned: unassigned || undefined,
    cf,
    created_from: isAssignedDate ? undefined : dateRange.from,
    created_to: isAssignedDate ? undefined : dateRange.to,
    assigned_from: isAssignedDate ? dateRange.from : undefined,
    assigned_to_date: isAssignedDate ? dateRange.to : undefined,
    date_field: dateRange.dateField,
    // Exact, not the planner estimate: with a filter applied the estimate can
    // be out by an order of magnitude, and the pager counts against this.
    include_count: true,
  };

  const { data, isPending, isFetching, error } = useLeads(query);

  // Same filter, narrowed to untouched — only asked for while the banner is up.
  const untouchedQuery: LeadQuery = { ...query, status: [UNTOUCHED], cursor: null, limit: 1 };
  const { data: untouchedData } = useLeads(untouchedQuery, untouchedMode);
  const untouchedTotal = untouchedData?.page_info.total;

  const byKey = useMemo(() => new Map(fields.map((f) => [f.api_key, f])), [fields]);

  // Column choice is per user and persisted locally; custom fields become
  // available as columns automatically.
  const { columns, visibleColumns, visibleIds, toggle, reset, ready } = useColumnPreferences(
    fields,
    user?.id,
  );

  const filterFields = useMemo(
    () =>
      fields
        .filter((f) => f.is_active && f.is_filterable && !HIDDEN_CUSTOM_FIELDS.has(f.api_key))
        .slice(0, 4),
    [fields],
  );

  function resetPaging() {
    setCursorStack([]);
    clearSelection();
  }

  function clearSelection() {
    setSelected(new Set());
    setUntouchedMode(false);
    setAllUntouchedSelected(false);
    setAllUnassignedSelected(false);
  }

  function changePageSize(size: number) {
    setPageSize(size);
    try {
      localStorage.setItem(PAGE_SIZE_KEY, String(size));
    } catch {
      // Private browsing or a full quota — the choice just won't persist.
    }
    resetPaging();
  }

  function toggleSelected(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll(ids: string[]) {
    setSelected((prev) => (ids.every((id) => prev.has(id)) ? new Set() : new Set(ids)));
  }

  const activeFilterChips: Array<{ label: string; clear: () => void }> = [];
  if (search) activeFilterChips.push({ label: `Search: ${search}`, clear: () => { setSearch(''); resetPaging(); } });
  if (unassigned) activeFilterChips.push({ label: 'Unassigned only', clear: () => { setUnassigned(false); resetPaging(); } });
  for (const cid of campaignIds) {
    activeFilterChips.push({
      label: `Campaign: ${campaigns.find((c) => c.id === cid)?.name ?? 'Unknown'}`,
      clear: () => { setCampaignIds(campaignIds.filter((x) => x !== cid)); resetPaging(); },
    });
  }
  for (const id of assignedTo) {
    const person = people.find((u) => u.id === id);
    activeFilterChips.push({
      label: `Assigned to: ${person?.full_name ?? 'someone'}`,
      clear: () => { setAssignedTo(assignedTo.filter((x) => x !== id)); resetPaging(); },
    });
  }
  if (dateRange.from || dateRange.to) {
    const from = dateRange.from ? shortDate(dateRange.from) : null;
    const to = dateRange.to ? shortDate(dateRange.to) : null;
    const prefix = dateRange.dateField === 'assigned' ? 'Assigned' : 'Created';
    const label =
      from && to
        ? from === to
          ? `${prefix}: ${from}`
          : `${prefix}: ${from} – ${to}`
        : from
          ? `${prefix} since ${from}`
          : `${prefix} until ${to}`;
    activeFilterChips.push({ label, clear: () => { setDateRange({}); resetPaging(); } });
  }
  for (const s of status) {
    activeFilterChips.push({ label: `Status: ${s.replace(/_/g, ' ')}`, clear: () => { setStatus(status.filter((x) => x !== s)); resetPaging(); } });
  }
  for (const sid of sourceIds) {
    activeFilterChips.push({
      label: `Source: ${sources.find((s) => s.id === sid)?.name ?? 'Unknown'}`,
      clear: () => { setSourceIds(sourceIds.filter((x) => x !== sid)); resetPaging(); },
    });
  }
  for (const [key, value] of Object.entries(cf)) {
    if (!value) continue;
    const field = byKey.get(key);
    const shown = field?.options.find((o) => o.value === value)?.label ?? String(value);
    activeFilterChips.push({
      label: `${field?.label ?? key}: ${shown}`,
      clear: () => { const next = { ...cf }; delete next[key]; setCf(next); resetPaging(); },
    });
  }

  const leads = data?.data ?? [];
  const pageUntouched = leads.filter((l) => l.status.key.toLowerCase() === UNTOUCHED);

  // "Unassigned only" is a bulk-assignment gesture, not just a filter: it
  // pre-selects the page so a manager can hand out a page at a time. An effect
  // rather than the handler, because checking the box changes the query and
  // the rows to select do not exist yet at click time. It deliberately does
  // not depend on allUnassignedSelected — re-running after a manual Clear
  // would undo the clear.
  useEffect(() => {
    if (!unassigned || !can('lead.assign') || allUnassignedSelected) return;
    setSelected(new Set((data?.data ?? []).map((l) => l.id)));
  }, [unassigned, data]);

  function selectAllUnassigned() {
    leadIds.mutate(
      { ...query, cursor: null, limit: undefined },
      {
        onSuccess: (res) => {
          setSelected(new Set(res.data));
          setAllUnassignedSelected(true);
        },
      },
    );
  }

  function toggleUntouchedMode(on: boolean) {
    setUntouchedMode(on);
    setAllUntouchedSelected(false);
    setSelected(on ? new Set(pageUntouched.map((l) => l.id)) : new Set());
  }

  function selectAllUntouched() {
    leadIds.mutate(
      { ...query, status: [UNTOUCHED], cursor: null, limit: undefined },
      {
        onSuccess: (res) => {
          setSelected(new Set(res.data));
          setAllUntouchedSelected(true);
        },
      },
    );
  }

  // The first row of a page is (pages behind it x page size) + 1. Safe because
  // every page but the last is full.
  const firstRow = cursorStack.length * pageSize + 1;
  const total = data?.page_info.total;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Leads</h1>
          <p className="sub">
            {user?.scope === 'own'
              ? 'Leads assigned to you'
              : user?.scope === 'team'
                ? "Your teams' leads"
                : 'All leads in the organisation'}
          </p>
        </div>
        <div className="page-head-actions">
          {isFetching && !isPending && <span className="spinner" title="Refreshing" />}
          <ColumnPicker
            columns={columns}
            visibleIds={visibleIds}
            onToggle={toggle}
            onReset={reset}
          />
          {can('lead.create') && (
            <button className="btn btn-primary" onClick={() => setShowNew(true)}>
              + New lead
            </button>
          )}
        </div>
      </div>

      <ErrorBanner error={error ?? bulkAssign.error ?? distribute.error ?? leadIds.error} />

      {createdNotice && (
        <div className="banner banner-info">
          <div>Lead created. It is unassigned until a manager or admin assigns it.</div>
        </div>
      )}

      {distribute.data && (
        <div className="banner banner-info">
          <div>
            Distributed {distribute.data.assigned} lead{distribute.data.assigned === 1 ? '' : 's'} across{' '}
            {distribute.data.team_name}:{' '}
            {distribute.data.by_member.map((m) => `${m.full_name} (${m.count})`).join(', ')}
            {distribute.data.skipped.length > 0 && ` — ${distribute.data.skipped.length} skipped`}
          </div>
        </div>
      )}

      <div className="filters">
        <div className="f grow">
          <label htmlFor="q">Search</label>
          <input
            id="q"
            placeholder="Name, phone or email…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); resetPaging(); }}
          />
        </div>

        <div className="f">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            value={status[0] ?? ''}
            onChange={(e) => { setStatus(e.target.value ? [e.target.value] : []); resetPaging(); }}
          >
            <option value="">Any status</option>
            {statuses.map((s) => (
              <option key={s.key} value={s.key}>{s.name}</option>
            ))}
          </select>
        </div>

        <div className="f">
          <label htmlFor="source">Source</label>
          <select
            id="source"
            value={sourceIds[0] ?? ''}
            onChange={(e) => { setSourceIds(e.target.value ? [e.target.value] : []); resetPaging(); }}
          >
            <option value="">Any source</option>
            {sources.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.lead_count.toLocaleString()})
              </option>
            ))}
          </select>
        </div>

        {/* Active campaigns, plus any deactivated one still on leads in view,
            so leads tagged before it was switched off can still be found. */}
        {campaigns.length > 0 && (
          <div className="f">
            <label htmlFor="campaign">Campaign</label>
            <select
              id="campaign"
              value={campaignIds[0] ?? ''}
              onChange={(e) => {
                setCampaignIds(e.target.value ? [e.target.value] : []);
                resetPaging();
              }}
            >
              <option value="">Any campaign</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.is_active ? '' : ' (deactivated)'} ({c.lead_count.toLocaleString()})
                </option>
              ))}
            </select>
          </div>
        )}

        {canFilterByAssignee && (
          <div className="f">
            <label htmlFor="assigned-to">Assigned to</label>
            <select
              id="assigned-to"
              value={assignedTo[0] ?? ''}
              onChange={(e) => {
                setAssignedTo(e.target.value ? [e.target.value] : []);
                // The two contradict each other, and the API lets "unassigned"
                // win, so clear it rather than silently ignore the person.
                if (e.target.value) setUnassigned(false);
                resetPaging();
              }}
            >
              <option value="">Anyone</option>
              {assignees.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name}
                  {u.id === user?.id ? ' (you)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Filters are generated from the registry — a field an admin adds
            today becomes filterable here with no code change. */}
        {filterFields.map((field) => (
          <div className="f" key={field.api_key}>
            <label htmlFor={`filter-${field.api_key}`}>{field.label}</label>
            {field.options.length > 0 ? (
              <select
                id={`filter-${field.api_key}`}
                value={(cf[field.api_key] as string) ?? ''}
                onChange={(e) => {
                  const next = { ...cf };
                  if (e.target.value) next[field.api_key] = e.target.value;
                  else delete next[field.api_key];
                  setCf(next);
                  resetPaging();
                }}
              >
                <option value="">Any</option>
                {field.options.filter((o) => o.is_active).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : TEXT_FILTER_CHOICES[field.api_key] ? (
              <select
                id={`filter-${field.api_key}`}
                value={String((cf[field.api_key] as { contains?: string } | undefined)?.contains ?? '')}
                onChange={(e) => {
                  const next = { ...cf };
                  if (e.target.value) next[field.api_key] = { contains: e.target.value };
                  else delete next[field.api_key];
                  setCf(next);
                  resetPaging();
                }}
              >
                <option value="">Any</option>
                {TEXT_FILTER_CHOICES[field.api_key]!.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <input
                id={`filter-${field.api_key}`}
                placeholder="Any"
                defaultValue={(cf[field.api_key] as string) ?? ''}
                onBlur={(e) => {
                  const next = { ...cf };
                  if (e.target.value) next[field.api_key] = { contains: e.target.value };
                  else delete next[field.api_key];
                  setCf(next);
                  resetPaging();
                }}
              />
            )}
          </div>
        ))}

        <div className="f" style={{ minWidth: 'auto' }}>
          <label>&nbsp;</label>
          <DateRangeFilter value={dateRange} onChange={(r) => { setDateRange(r); resetPaging(); }} />
        </div>

        {can('lead.assign') && (
          <div className="f" style={{ minWidth: 'auto' }}>
            <label htmlFor="unassigned">&nbsp;</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 400, marginBottom: 0, whiteSpace: 'nowrap' }}>
              <input
                id="unassigned"
                type="checkbox"
                checked={unassigned}
                onChange={(e) => {
                  setUnassigned(e.target.checked);
                  if (e.target.checked) setAssignedTo([]);
                  resetPaging();
                }}
              />
              Unassigned only
            </label>
          </div>
        )}
      </div>

      {activeFilterChips.length > 0 && (
        <div className="active-filters">
          {activeFilterChips.map((chip, i) => (
            <button key={i} className="chip chip-plain" onClick={chip.clear} title="Remove filter">
              {chip.label} ✕
            </button>
          ))}
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => { setSearch(''); setStatus([]); setSourceIds([]); setAssignedTo([]); setCampaignIds([]); setUnassigned(false); setCf({}); setDateRange({}); resetPaging(); }}
          >
            Clear all
          </button>
        </div>
      )}

      {can('lead.assign') && selected.size > 0 && (
        <div className="bulk-bar">
          <span className="count">{selected.size} selected</span>
          <div className="spacer" />
          {user?.scope !== 'own' && (
            <button
              className="btn btn-sm"
              disabled={distribute.isPending}
              onClick={() => setShowDistribute(true)}
            >
              {distribute.isPending ? <span className="spinner" /> : 'Distribute evenly…'}
            </button>
          )}
          <button className="btn btn-sm btn-primary" onClick={() => setShowBulkAssign(true)}>
            Assign to…
          </button>
          <button className="btn btn-ghost btn-sm" onClick={clearSelection}>
            Clear
          </button>
        </div>
      )}

      {can('lead.assign') && !unassigned && pageUntouched.length > 0 && (
        <div className="select-bar">
          <label className="check">
            <input
              type="checkbox"
              checked={untouchedMode}
              onChange={(e) => toggleUntouchedMode(e.target.checked)}
            />
            Select untouched on this page ({pageUntouched.length})
          </label>
        </div>
      )}

      {unassigned && can('lead.assign') && selected.size > 0 && (
        <div className="banner banner-flag">
          <div>
            {allUnassignedSelected
              ? `All ${selected.size.toLocaleString()} unassigned leads matching these filters are selected.`
              : `${selected.size.toLocaleString()} unassigned lead${selected.size === 1 ? '' : 's'} on this page selected.`}
          </div>
          <div className="spacer" />
          {!allUnassignedSelected && total !== undefined && total > leads.length && (
            <button className="btn btn-sm" disabled={leadIds.isPending} onClick={selectAllUnassigned}>
              {leadIds.isPending ? (
                <span className="spinner" />
              ) : (
                `Select all ${total.toLocaleString()} unassigned leads`
              )}
            </button>
          )}
          {allUnassignedSelected && leadIds.data?.truncated && (
            <span className="hint">Capped at the first {selected.size.toLocaleString()}.</span>
          )}
        </div>
      )}

      {untouchedMode && (
        <div className="banner banner-flag">
          <div>
            {allUntouchedSelected
              ? `All ${selected.size.toLocaleString()} untouched leads matching these filters are selected.`
              : `${pageUntouched.length} untouched lead${pageUntouched.length === 1 ? '' : 's'} on this page selected.`}
          </div>
          <div className="spacer" />
          {!allUntouchedSelected && untouchedTotal !== undefined && untouchedTotal > pageUntouched.length && (
            <button className="btn btn-sm" disabled={leadIds.isPending} onClick={selectAllUntouched}>
              {leadIds.isPending ? (
                <span className="spinner" />
              ) : (
                `Select all ${untouchedTotal.toLocaleString()} untouched leads`
              )}
            </button>
          )}
          {leadIds.data?.truncated && allUntouchedSelected && (
            <span className="hint">Capped at the first {selected.size.toLocaleString()}.</span>
          )}
        </div>
      )}

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              {can('lead.assign') && (
                <th className="row-check">
                  <input
                    type="checkbox"
                    aria-label="Select all on this page"
                    checked={leads.length > 0 && leads.every((l) => selected.has(l.id))}
                    onChange={() => toggleSelectAll(leads.map((l) => l.id))}
                  />
                </th>
              )}
              {visibleColumns.map((column) => (
                <th key={column.id} style={column.align === 'right' ? { textAlign: 'right' } : undefined}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>

          {isPending || !ready ? (
            <TableSkeleton cols={Math.max(visibleColumns.length, 5) + (can('lead.assign') ? 1 : 0)} />
          ) : (
            <tbody>
              {leads.map((lead) => (
                <tr
                  key={lead.id}
                  className={
                    UNWORKED_STATUSES.has(lead.status.key.toLowerCase())
                      ? 'clickable row-unworked'
                      : 'clickable'
                  }
                  onClick={() => openLead(lead.id)}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && openLead(lead.id)}
                >
                  {can('lead.assign') && (
                    <td className="row-check" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${lead.full_name}`}
                        checked={selected.has(lead.id)}
                        onChange={() => toggleSelected(lead.id)}
                      />
                    </td>
                  )}
                  {visibleColumns.map((column) => (
                    <td
                      key={column.id}
                      className={column.align === 'right' ? 'num' : undefined}
                      style={column.align === 'right' ? { textAlign: 'right' } : undefined}
                    >
                      {column.render(lead, { byKey })}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
        </table>

        {!isPending && leads.length === 0 && (
          <EmptyState title="No leads match">
            {activeFilterChips.length > 0
              ? 'Try removing a filter — custom-field filters are exact matches.'
              : user?.scope === 'own'
                ? 'Nothing is assigned to you yet.'
                : 'Leads will appear here as they arrive from Meta or are added manually.'}
          </EmptyState>
        )}
      </div>

      {/* Cursor paging. No page numbers: the API is keyset-paginated, so
          "jump to page 17" is not expressible. Previous walks back through
          the cursors we have already seen. */}
      <div className="pager">
        <span className="count">
          {total !== undefined && leads.length > 0
            ? `${firstRow.toLocaleString()}–${(firstRow + leads.length - 1).toLocaleString()} of ${total.toLocaleString()} leads`
            : total !== undefined
              ? 'No leads'
              : `${leads.length} shown`}
        </span>
        <label className="page-size">
          Rows
          <select
            aria-label="Rows per page"
            value={pageSize}
            onChange={(e) => changePageSize(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
        <button
          className="btn btn-sm"
          disabled={cursorStack.length === 0 || isFetching}
          onClick={() => setCursorStack((s) => s.slice(0, -1))}
        >
          ← Previous
        </button>
        <button
          className="btn btn-sm"
          disabled={!data?.page_info.has_more || isFetching}
          onClick={() => {
            const next = data?.page_info.next_cursor;
            if (next) setCursorStack((s) => [...s, next]);
          }}
        >
          Next →
        </button>
      </div>

      {showNew && (
        <NewLeadModal
          onClose={() => setShowNew(false)}
          onCreated={(id, merged) => {
            setShowNew(false);
            // New leads start unassigned; someone who only sees their own
            // leads could not open it. A merge lands on a lead that exists.
            if (user?.scope === 'own' && !merged) {
              setCreatedNotice(true);
              return;
            }
            navigate(`/leads/${id}`, { state: { merged } });
          }}
        />
      )}

      {showDistribute && (
        <DistributeModal
          count={selected.size}
          busy={distribute.isPending}
          onClose={() => setShowDistribute(false)}
          onConfirm={(teamIds) =>
            distribute.mutate(
              { lead_ids: [...selected], team_ids: teamIds },
              {
                onSuccess: () => {
                  setShowDistribute(false);
                  clearSelection();
                },
              },
            )
          }
        />
      )}

      {showBulkAssign && (
        <BulkAssignModal
          count={selected.size}
          busy={assigning}
          onClose={() => setShowBulkAssign(false)}
          onAssign={async (userIds) => {
            if (assigning) return;
            setAssigning(true);
            // Deal the leads out in turn, then one bulk-assign per person.
            const ids = [...selected];
            const shares = userIds.map((userId, i) => ({
              userId,
              leadIds: ids.filter((_, n) => n % userIds.length === i),
            }));
            try {
              for (const share of shares) {
                if (share.leadIds.length === 0) continue;
                await bulkAssign.mutateAsync({ lead_ids: share.leadIds, user_id: share.userId });
              }
              setShowBulkAssign(false);
              setSelected(new Set());
            } catch {
              // The error banner reads bulkAssign.error; leads already handed
              // to earlier people stay assigned.
            } finally {
              setAssigning(false);
            }
          }}
        />
      )}
    </div>
  );
}
